import { useEffect, useRef } from "react";

import { Cross, Tick } from "components/icons/generated";
import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemLink from "components/ListEntitiesItem/Link";
import Box from "ds/components/Box";
import { useDrawerVisibilityForId } from "ds/components/DrawerNew/useDrawerVisibilityForId";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import Timestamp from "components/Timestamp";
import { AccessResult } from "utils/access";

import WebhookDropdown from "../WebhookDropdown";
import { COLUMN_ORDER } from "./constants";
import { WebhookIntegrationWithTimestamp } from "./types";

type WebhookListItemProps = {
  item: WebhookIntegrationWithTimestamp;
  setRowHeight?: (size: number) => void;
  moduleId: string;
  onEditClick: (item: WebhookIntegrationWithTimestamp) => void;
  onDetailsClick: (item: WebhookIntegrationWithTimestamp) => void;
  canManageModule: AccessResult;
};

const refetchQueriesOnDelete = ["GetModuleWebhooks"];

const WebhookListItem = ({
  item,
  setRowHeight,
  moduleId,
  onEditClick,
  onDetailsClick,
  canManageModule,
}: WebhookListItemProps) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const isActive = useDrawerVisibilityForId(item.id);

  const handleRowHeight = () => {
    if (setRowHeight && rowRef.current) {
      setRowHeight(rowRef.current.getBoundingClientRect().height);
    }
  };

  // on every render, update the row height
  useEffect(handleRowHeight);

  return (
    <ListEntitiesItem
      direction="row"
      align="center"
      justify="between"
      grid
      gridTemplate={COLUMN_ORDER}
      gap="0 large"
      ref={rowRef}
      isActive={isActive}
      role="row"
    >
      <Box align="center" role="rowheader">
        <ListEntitiesItemLink
          href={`/module/${moduleId}/settings/integrations/webhook/${item.id}`}
          title={item.endpoint}
        />
      </Box>

      <Box role="cell" align="center" gap="small">
        {item.enabled ? (
          <>
            <Icon src={Tick} color="success" />
            <Typography tag="span" variant="p-body2" color="success">
              Enabled
            </Typography>
          </>
        ) : (
          <>
            <Icon src={Cross} color="danger" />
            <Typography tag="span" variant="p-body2" color="danger">
              Disabled
            </Typography>
          </>
        )}
      </Box>

      <Box role="cell" align="center">
        <Timestamp timestamp={item.createdAt} variant="p-body2" />
      </Box>

      <Box role="cell" align="end">
        <WebhookDropdown
          id={item.id}
          endpoint={item.endpoint}
          refetchQueriesOnDelete={refetchQueriesOnDelete}
          onEditClick={() => onEditClick(item)}
          onDetailsClick={() => onDetailsClick(item)}
          moduleId={moduleId}
          canManageModule={canManageModule}
        />
      </Box>
    </ListEntitiesItem>
  );
};

export default WebhookListItem;
